import Image from '@/components/common/SpiritoryImage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faRightToBracket,
  faUserPlus,
  faArrowRight,
} from '@fortawesome/free-solid-svg-icons';
import styles from './seller-guide.module.scss';
import Link from 'next/link';
import translationFallback from '@/utilites/translations/translationFallback';
import { useTranslation } from 'next-i18next';
import SearchInGuides from '@/components/common/searchInGuides';
import sellerGuideImage from '@/public/images/sell-guide/seller-guide.webp';
import { useRouter } from 'next/router';
import mainBannerImageMobile from '@/public/images/sell-guide/mainBannerMobile.webp';
import Head from 'next/head';
import Accordion from 'react-bootstrap/Accordion';
import getBasicStaticSideProps from '@/lib/staticSideProps/getBasicStaticSideProps';
import useBackendUser from '@/hooks/useBackendUser';
import SellerGuideStructuredData from '../components/structured_data/seller_guide_structured_data';
import getCanonicalUrl from '@/utilites/seo/get-canonical-url';
import LanguageAlternates from '@/components/seo/LanguageAlternates';

function SellerGuide() {
  const { data: user } = useBackendUser();
  const { t } = useTranslation('seller_guide');

  const router = useRouter();
  const canonicalUrl = getCanonicalUrl(router);

  return (
    <>
      <Head>
        <title>
          {t('seller_guide_title')}
        </title>
        <meta
          name="description"
          content={t('seller_guide_description')}
        />
        <meta
          name="keywords"
          content={t('seller_guide_keywords')}
        />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={t('seller_guide_title')} />
        <meta property="og:description" content={t('seller_guide_description')} />
        <meta property="og:image" content="https://spiritory.com/logo.png" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="Spiritory" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('seller_guide_title')} />
        <meta name="twitter:description" content={t('seller_guide_description')} />
        <meta name="twitter:image" content="https://spiritory.com/logo.png" />
        <meta name="twitter:site" content="@spiritoryGlobal" />
        <link rel="canonical" href={canonicalUrl} />
        <LanguageAlternates />
        <SellerGuideStructuredData />
      </Head>
      <div className={styles.mainContainer}>
        <div
          id="seller-Section1"
          className={`bg-blue-gradient d-flex flex-sm-row flex-column-reverse justify-content-end justify-content-sm-between align-items-center mw-auto mh-auto ${styles.mainSection} ${styles.textColor}`}
          style={{ height: '762px', width: 'auto' }}
        >
          <div className={`tw-flex tw-flex-col tw-justify-center tw-flex-1 tw-items-center !tw-pl-[60px] max-nine:!tw-pl-2 tw-mb-5 max-nine:tw-text-center container `}>
            <div className="container d-flex align-items-end">
              <h1 className="h2">
                {t('seller_guide_h1')}
              </h1>
            </div>
            <div className="container">
              <div className="d-flex">
                <h2 className="font-size-20 ">
                  {t('simple_quick_selling')}
                </h2>
              </div>
              <div className={`d-flex gap-2 max-nine:tw-justify-center  ${styles.btnContainer}`}>
                <div
                  onClick={() => {
                    if (user) {
                      router.push('/dashboard');
                    } else {
                      router.push('/auth/sign-in');
                    }
                  }}
                >
                  <button
                    className={`btn btn-s-outline-primary my-2 ${styles.btnSize}`}
                    type="button"
                  >
                    <span>
                      <FontAwesomeIcon icon={faRightToBracket} />
                    </span>
                    {translationFallback('log_in', t('log_in'), 'Log In')}
                  </button>
                </div>
                <div
                  onClick={() => {
                    if (user) {
                      router.push('/dashboard');
                    } else {
                      router.push('/auth/sign-up');
                    }
                  }}
                >
                  <button className={`btn btn-s-primary my-2 ${styles.btnSize}`} type="button">
                    <span>
                      <FontAwesomeIcon icon={faUserPlus} />
                    </span>
                    {translationFallback('sign_up', t('sign_up'), 'Sign Up')}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className={`mb-sm-0 mb-5 ${styles.mainImage} ${styles.mainImageContainer}`}>
            <Image
              src={sellerGuideImage}
              alt="example"
              width={0}
              height={0}
              sizes="100vw"
              className={styles.hideMobile}
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
            <Image
              src={mainBannerImageMobile}
              alt="example"
              width={1000}
              height={1000}
              sizes="100vw"
              className={styles.hideDesktop}
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          </div>
        </div>

        <div
          className={`pt-9 d-flex align-items-center justify-content-between gap-8 ${styles.firstSectionImage}`}
        >
          <div>
            <Image
              src="/images/sell-guide/section1.svg"
              alt="example"
              width={0}
              height={0}
              sizes="100vw"
            />
          </div>
          <div className="position-relative d-flex flex-column">
            <span className={styles.twoNumber}>1</span>
            <div
              style={{ width: 'fit-content' }}
              className="d-flex flex-column align-items-start text-black"
            >
              <h2
                className="h3 mt-3"
                style={{
                  fontWeight: '700',
                  fontSize: '32px',
                  lineHeight: '40px',
                }}
              >
                1.{' '}
                {translationFallback(
                  'finding_your_bottle',
                  t('finding_your_bottle'),
                  '1.Finding your bottle'
                )}
              </h2>
              <div
                className="mt-2"
                style={{ width: '70%', height: 4, backgroundColor: '#C2AD7D' }}
              ></div>
            </div>
            <div className="mt-3">
              <div className="d-flex mb-2">
                <p className="font-size-20 lh-23 text-black">
                  {translationFallback(
                    'browse_and_select',
                    t('browse_and_select'),
                    'Simply browse our growing marketplace and select the bottle you`re looking to sell.'
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div id="seller-Section3" className={`${styles.cantFindCard} position-relative`}>
          <div className={`px-5`}>
            <h3 className={`h3 font-size-36 font-jakarta lh-40 ${styles.bold} `}>
              {translationFallback(
                'cant_find_right_bottle',
                t('cant_find_right_bottle'),
                "Can't find the right bottle?"
              )}
            </h3>
            <p className="font-size-20 lh-25 my-3">
              {translationFallback(
                'offer_your_bottle',
                t('offer_your_bottle'),
                'Offer your bottle and thereby help us become better for you'
              )}
            </p>

            <div className={`${styles.cantFindCardText}`}>
              <Link
                style={{ textDecoration: 'none' }}
                href={user ? '/dashboard/suggest-bottle' : '/auth/sign-in'}
                className="d-flex justify-content-between"
              >
                <div className={styles.cantFindBlueText}>
                  {translationFallback(
                    'suggest_a_bottle',
                    t('suggest_a_bottle'),
                    'Suggest a bottle'
                  )}
                </div>
                <div className={styles.cantFindBlueText}>
                  <FontAwesomeIcon icon={faArrowRight} />
                </div>
              </Link>
              <div
                style={{
                  width: '100%',
                  height: 2,
                  backgroundColor: '#1C5CEB',
                  marginTop: 5,
                }}
              ></div>
            </div>
          </div>
          <div className={styles.bottlesImg}>
            <div className={styles.bottlesImg}>
              <Image
                src="/images/sell-guide/bottles.svg"
                alt="example"
                width={0}
                height={0}
                sizes="100vw"
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            </div>
          </div>
          <div className={styles.backgroundImage}>
            <Image
              src="/images/sell-guide/bottles.svg"
              alt="example"
              width={0}
              height={0}
              sizes="100vw"
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          </div>
        </div>
        <div
          className={`d-flex align-items-center justify-content-between gap-8 ${styles.secondSectionImage}`}
        >
          <div className="position-relative">
            <span className={styles.oneNumber}>2</span>
            <div style={{ width: 'fit-content' }} className="d-flex flex-column align-items-start">
              <h2
                className="h3 mt-3"
                style={{
                  fontWeight: '700',
                  fontSize: '32px',
                  lineHeight: '40px',
                }}
              >
                {translationFallback(
                  'the_product_page',
                  t('the_product_page'),
                  '2.The product page'
                )}
              </h2>
              <div
                className={`mt-2 ${styles.borderDown}`}
                style={{ width: '70%', height: 4, backgroundColor: '#C2AD7D' }}
              ></div>
            </div>
            <div className={`mt-3 ${styles.secondSectionText}`}>
              <div className="d-flex mb-2">
                <p className="font-size-20 lh-23">
                  {translationFallback(
                    'product_page_description',
                    t('product_page_description'),
                    'Once you\'ve found your desired bottle, you can decide on how you want to sell it. Your two options are to either "sell now" or "ask". We will explain the two options now:'
                  )}
                </p>
              </div>
            </div>
          </div>
          <div>
            <Image
              src="/images/sell-guide/section2.svg"
              alt="example"
              width={0}
              height={0}
              sizes="100vw"
            />
          </div>
        </div>

        <div
          style={{
            background: '#1C5CEB',
            padding: '6rem 1rem',
          }}
          className={styles.blueSection}
        >
          <div className="h2 py-5">
            <span>{translationFallback('option_1_title', t('option_1_title'), 'Option 1')}</span>
          </div>
          <div
            className={`pt-9 d-flex align-items-center justify-content-between gap-8 ${styles.firstSectionImage}`}
            style={{ paddingTop: 0 }}
          >
            <div className={styles.specialImage}>
              <Image
                src="/images/sell-guide/option1bot.svg"
                alt="example"
                width={0}
                height={0}
                sizes="100vw"
              />
              <Image
                src="/images/sell-guide/option1Tab.svg"
                alt="example"
                width={0}
                height={0}
                sizes="100vw"
              />
            </div>
            <div className="position-relative d-flex flex-column">
              <span className={styles.twoNumber} style={{ color: '#FFFFFF33' }}>
                3
              </span>
              <div
                style={{ width: 'fit-content' }}
                className="d-flex flex-column align-items-start"
              >
                <h2
                  className="h3 mt-3 font-jakarta"
                  style={{
                    fontWeight: '700',
                    fontSize: '32px',
                    lineHeight: '40px',
                  }}
                >
                  {translationFallback('sell_now_title', t('sell_now_title'), '3.Sell now')}
                </h2>
                <div
                  className="mt-2"
                  style={{
                    width: '70%',
                    height: 4,
                    backgroundColor: '#C2AD7D',
                  }}
                ></div>
              </div>
              <div className="mt-3">
                <div className="d-flex mb-2">
                  <p className="font-size-20 lh-23">
                    {translationFallback(
                      'sellInstantlyText',
                      t('sellInstantlyText'),
                      `Sell instantly at the highest "Bid" of a buyer`
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="h2 py-5" style={{ marginTop: '3rem' }}>
            <span>{translationFallback('option_2_title', t('option_2_title'), 'Option 2')}</span>
          </div>
          <div
            className={`d-flex align-items-center justify-content-between gap-8 ${styles.secondSectionImage}`}
          >
            <div className="position-relative">
              <span className={styles.oneNumber} style={{ color: '#FFFFFF33', zIndex: 1 }}>
                4
              </span>
              <div
                style={{ width: 'fit-content' }}
                className="d-flex flex-column align-items-start"
              >
                <h2
                  className="h3 mt-3"
                  style={{
                    fontWeight: '700',
                    fontSize: '32px',
                    lineHeight: '40px',
                  }}
                >
                  {translationFallback('place_ask_title', t('place_ask_title'), '4.Place Ask')}
                </h2>
                <div
                  className={`mt-2 ${styles.borderDown}`}
                  style={{
                    width: '70%',
                    height: 4,
                    backgroundColor: '#C2AD7D',
                  }}
                ></div>
              </div>
              <div className={`mt-3 ${styles.secondSectionText}`}>
                <div className="d-flex mb-2">
                  <p className="font-size-20 lh-23">
                    {translationFallback(
                      'start_trades_steps',
                      t('start_trades_steps'),
                      'Start your first trades with these easy steps'
                    )}
                  </p>
                </div>
                <ul className="font-size-20 lh-25">
                  <li className="py-1">
                    {translationFallback(
                      'placing_ask',
                      t('placing_ask'),
                      'When placing an "ask", you decide the price you\'re selling at.'
                    )}
                  </li>
                  <li className="py-1">
                    {translationFallback(
                      'matching_buyers_sellers',
                      t('matching_buyers_sellers'),
                      'We match buyers with the lowest ask and sellers with the highest bid!'
                    )}
                  </li>
                  <li className="py-1">
                    {translationFallback(
                      'lowest_ask_sold',
                      t('lowest_ask_sold'),
                      'If you have the lowest ask, buyers can match your ask via "buy now" and your bottle is sold.'
                    )}
                  </li>
                </ul>
              </div>
            </div>
            <div className={`${styles.specialImageTwo}`}>
              <Image
                src="/images/sell-guide/option1bot.svg"
                alt="example"
                width={0}
                height={0}
                sizes="100vw"
              />
              <Image
                src="/images/sell-guide/option2Tab.svg"
                alt="example"
                width={0}
                height={0}
                sizes="100vw"
              />
            </div>
          </div>
        </div>

        <div
          className={`py-9 d-flex align-items-center justify-content-between gap-8 ${styles.firstSectionImage}`}
        >
          <div>
            <Image
              src="/images/sell-guide/section5.svg"
              alt="example"
              width={0}
              height={0}
              sizes="100vw"
            />
          </div>
          <div className="position-relative d-flex flex-column">
            <span className={styles.twoNumber}>5</span>
            <div
              style={{ width: 'fit-content' }}
              className="d-flex flex-column align-items-start text-black"
            >
              <h2
                className="h3 mt-3"
                style={{
                  fontWeight: '700',
                  fontSize: '32px',
                  lineHeight: '40px',
                }}
              >
                {translationFallback('bottle_sold_title', t('bottle_sold_title'), '5.Bottle sold?')}
              </h2>
              <div
                className="mt-2"
                style={{ width: '70%', height: 4, backgroundColor: '#C2AD7D' }}
              ></div>
            </div>
            <div className="mt-3">
              <div className="d-flex mb-2">
                <p className="font-size-20 lh-23 text-black">
                  {translationFallback(
                    'conduct_payout_steps',
                    t('conduct_payout_steps'),
                    'To conduct your payout, follow these 2 simple steps:'
                  )}
                </p>
              </div>
              <ul className="font-size-20 lh-25 py-3">
                <li className="py-1">
                  {translationFallback(
                    'authenticating_bottle',
                    t('authenticating_bottle'),
                    'Authenticating your bottle'
                  )}
                </li>
                <li className="py-1">
                  {translationFallback(
                    'shipping_bottle',
                    t('shipping_bottle'),
                    'Shipping the bottle'
                  )}
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div
          id="seller-Section8"
          className={`${styles.cardsSection}`}
          style={{ padding: '0px 0px' }}
        >
          <div
            className="card col-12 col-sm-6"
            style={{ height: '784px', maxWidth: '556px', margin: '12px' }}
          >
            <h2
              className={`card-title ${styles.bold} d-flex justify-content-center align-items-center tw-text-2xl`}
              style={{ height: '60px' }}
            >
              {translationFallback(
                'authenticating_your_bottle_title',
                t('authenticating_your_bottle_title'),
                '6.Authenticating your bottle'
              )}
            </h2>
            <div
              className={`overflow-hidden position-relative ${styles.blueContainerImg}`}
              style={{ minWidth: '100%', height: '245px', borderRadius: '0px' }}
            >
              <Image
                src="/images/sell-guide/card6.svg"
                alt="example"
                width={0}
                height={0}
                sizes="100vw"
                style={{ height: '155px', width: '100%' }}
                className="card-img-top"
              />
              <div
                className={`font-jakarta ${styles.containerTextNum}`}
                style={{
                  fontSize: '130.4px',
                  position: 'absolute',
                  right: '1rem',
                  bottom: '-1.5rem',
                  lineHeight: 'unset',
                }}
              >
                6
              </div>
            </div>
            <div className="card-body">
              <p
                className="card-text px-4 py-2 lh-25"
                style={{ fontSize: '20px', fontWeight: '500' }}
              >
                {translationFallback(
                  'go_to_your_sold_bottle',
                  t('go_to_your_sold_bottle'),
                  'Go to your sold bottle on the Sales page and select "Authenticate now".'
                )}
              </p>
              <div className={`px-4 ${styles.cantFindCardText}`} style={{ width: '100%' }}>
                <div className="d-flex justify-content-between py-2">
                  <Link
                    href={'/authenticating'}
                    className={`${styles.cantFindBlueText} `}
                    style={{
                      fontWeight: '500',
                      fontSize: '18px',
                      lineHeight: '23px',
                      textDecoration: 'none',
                    }}
                  >
                    {translationFallback(
                      'authenticate_now',
                      t('authenticate_now'),
                      'Authenticate now'
                    )}
                  </Link>
                  <div className={styles.cantFindBlueText}>
                    <FontAwesomeIcon icon={faArrowRight} />
                  </div>
                </div>
                <hr></hr>
              </div>
              <p
                className="card-text px-4 py-2 lh-25"
                style={{ fontSize: '20px', fontWeight: '500' }}
              >
                {translationFallback(
                  'authPageText.text1',
                  t('authPageText.text1'),
                  'On the Authentication page, simply upload your images and submit!'
                )}
              </p>
              <p
                className="card-text px-4 py-2"
                style={{
                  fontSize: '18px',
                  fontWeight: '400',
                  color: '#5F6D7E',
                }}
              >
                {translationFallback(
                  'authPageText.text2',
                  t('authPageText.text2'),
                  'Learn more about our quick and simple authentication process.'
                )}
              </p>
              <div className={`px-4 ${styles.cantFindCardText}`} style={{ width: '100%' }}>
                <div className="d-flex justify-content-between py-2">
                  <Link
                    href={'/authenticating'}
                    className={`${styles.cantFindBlueText} `}
                    style={{
                      fontWeight: '500',
                      fontSize: '18px',
                      lineHeight: '23px',
                      textDecoration: 'none',
                    }}
                  >
                    {translationFallback('learn_more', t('learn_more'), 'Learn more')}
                  </Link>
                  <div className={styles.cantFindBlueText}>
                    <FontAwesomeIcon icon={faArrowRight} />
                  </div>
                </div>
                <hr></hr>
              </div>
            </div>
          </div>
          <div
            className="card col-12 col-sm-6"
            style={{ height: '784px', maxWidth: '556px', margin: '12px' }}
          >
            <h2
              className={`card-title ${styles.bold} d-flex justify-content-center align-items-center tw-text-2xl`}
              style={{ height: '60px' }}
            >
              {translationFallback(
                'shipping_the_bottle_title',
                t('shipping_the_bottle_title'),
                '7. Shipping the bottle'
              )}
            </h2>
            <div
              className={`overflow-hidden ${styles.blueContainerImg} position-relative`}
              style={{ minWidth: '100%', height: '245px', borderRadius: '0px' }}
            >
              <Image
                src="/images/sell-guide/card7.svg"
                alt="example"
                width={0}
                height={0}
                sizes="100vw"
                style={{ height: '155px', width: '100%' }}
                className="card-img-top"
              />
              <div
                className={`font-work-sans ${styles.containerTextNum}`}
                style={{
                  fontSize: '130.4px',
                  position: 'absolute',
                  right: '1rem',
                  bottom: '-1.5rem',
                  lineHeight: 'unset',
                }}
              >
                7
              </div>
            </div>
            <div className="card-body">
              <p
                className="card-text px-4 py-2 lh-25"
                style={{ fontSize: '20px', fontWeight: '500' }}
              >
                {translationFallback(
                  'afterAuthenticationPart1',
                  t('afterAuthenticationPart1'),
                  "After authentication, we'll"
                )}{' '}
                <strong>
                  {translationFallback(
                    'shippingLabelText',
                    t('shippingLabelText'),
                    'shipping label'
                  )}
                </strong>{' '}
                {translationFallback(
                  'afterAuthenticationPart2',
                  t('afterAuthenticationPart2'),
                  'for your Bottle.'
                )}
              </p>
              <p
                className="card-text px-4 py-2 lh-25"
                style={{ fontSize: '20px', fontWeight: '500' }}
              >
                {translationFallback(
                  'afterAuthText2',
                  t('afterAuthText2'),
                  'Follow our easy shipping guidelines. Get paid when buyer confirms delivery.'
                )}
              </p>
              <p
                className="card-text px-4 py-2 lh-25"
                style={{
                  fontSize: '20px',
                  fontWeight: '400',
                  color: '#5F6D7E',
                }}
              >
                {translationFallback(
                  'shippingGuidelinesText',
                  t('shippingGuidelinesText'),
                  'Want to learn more about our shipping guidelines:'
                )}
              </p>
              <div className={`px-4 ${styles.cantFindCardText}`} style={{ width: '100%' }}>
                <div className="d-flex justify-content-between py-2">
                  <Link
                    href={'/shipping-guide'}
                    className={`${styles.cantFindBlueText} `}
                    style={{
                      fontWeight: '500',
                      fontSize: '18px',
                      lineHeight: '23px',
                      textDecoration: 'none',
                    }}
                  >
                    {translationFallback(
                      'shippingGuidelineTitle',
                      t('shippingGuidelineTitle'),
                      'Shipping Guideline'
                    )}
                  </Link>
                  <div className={styles.cantFindBlueText}>
                    <FontAwesomeIcon icon={faArrowRight} />
                  </div>
                </div>
                <hr></hr>
              </div>
            </div>
          </div>
        </div>

        <div
          className={`py-9 d-flex align-items-center justify-content-between gap-8 ${styles.firstSectionImage}`}
        >
          <div className={styles.specialSectionImage}>
            <Image
              src="/images/sell-guide/section8.svg"
              alt="example"
              width={0}
              height={0}
              sizes="100vw"
            />
            <Image
              src="/images/sell-guide/not-section8.svg"
              alt="example"
              width={0}
              height={0}
              sizes="100vw"
            />
          </div>
          <div className="position-relative d-flex flex-column">
            <span className={styles.twoNumber}>8</span>
            <div
              style={{ width: 'fit-content' }}
              className="d-flex flex-column align-items-start text-black"
            >
              <h2
                className="h3 mt-3"
                style={{
                  fontWeight: '700',
                  fontSize: '32px',
                  lineHeight: '40px',
                }}
              >
                {translationFallback('getPayoutTitle', t('getPayoutTitle'), '8. Get Payout')}
              </h2>
              <div
                className="mt-2"
                style={{ width: '70%', height: 4, backgroundColor: '#C2AD7D' }}
              ></div>
            </div>
            <div className="mt-3">
              <div className="d-flex mb-2">
                <p className="font-size-20 lh-23 text-black">
                  {translationFallback(
                    'getPayoutText',
                    t('getPayoutText'),
                    'You will receive your money automatically, when the buyer has confirmed the safe arrival.'
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div
          id="seller-Section10"
          className={`${styles.green} ${styles.textwhite} ${styles.greenContainer} px-0 py-6`}
          style={{ height: 'auto' }}
        >
          <div
            style={{ width: 'fit-content', margin: '0 auto' }}
            className={`container d-flex flex-column justify-content-center align-items-center mb-6 mt-2`}
          >
            <h2 className={`h2 text-center`}>
              {translationFallback(
                'sellingSpecialText',
                t('sellingSpecialText'),
                'What makes selling with us special:'
              )}
            </h2>
            <div
              className="mt-2"
              style={{ width: '70%', height: 4, backgroundColor: '#C2AD7D' }}
            ></div>
          </div>
          <div
            className={`d-flex justify-content-center align-items-center ${styles.allCards}`}
            style={{ gap: '32px' }}
          >
            <div
              className="col d-flex flex-column justify-content-center align-items-center"
              style={{
                background: '#FFFFFF1A',
                borderRadius: '20px',
                width: '359px',
                height: '193px',
              }}
            >
              <Image
                src="/images/sell-guide/gri1.svg"
                alt="example"
                width={0}
                height={0}
                sizes="100vw"
                style={{
                  width: '80px',
                  height: 'auto',
                  objectFit: 'contain',
                  margin: '22px',
                }}
              />
              <p className="mx-2">
                {translationFallback(
                  'transparencyText',
                  t('transparencyText'),
                  '100% transparency on all fees'
                )}
              </p>
            </div>
            <div
              className="col d-flex flex-column justify-content-center align-items-center"
              style={{
                background: '#FFFFFF1A',
                borderRadius: '20px',
                width: '359px',
                height: '193px',
              }}
            >
              <Image
                src="/images/sell-guide/gri2.svg"
                alt="example"
                width={0}
                height={0}
                sizes="100vw"
                style={{
                  width: '80px',
                  height: 'auto',
                  objectFit: 'contain',
                  margin: '22px',
                }}
              />
              <p className="mx-2">
                {translationFallback(
                  'authenticationText',
                  t('authenticationText'),
                  'Simple and quick authentication'
                )}
              </p>
            </div>
            <div
              className="col d-flex flex-column justify-content-center align-items-center"
              style={{
                background: '#FFFFFF1A',
                borderRadius: '20px',
                width: '359px',
                height: '193px',
              }}
            >
              <Image
                src="/images/sell-guide/gri3.svg"
                alt="example"
                width={0}
                height={0}
                sizes="100vw"
                style={{
                  width: '80px',
                  height: 'auto',
                  objectFit: 'contain',
                  margin: '22px',
                }}
              />
              <p className="mx-2">
                {translationFallback(
                  'payoutProcessText',
                  t('payoutProcessText'),
                  'Safe and secure payout process'
                )}
              </p>
            </div>
            <div
              className="col d-flex flex-column justify-content-center align-items-center"
              style={{
                background: '#FFFFFF1A',
                borderRadius: '20px',
                width: '359px',
                height: '193px',
              }}
            >
              <Image
                src="/images/sell-guide/gri4.svg"
                alt="example"
                width={0}
                height={0}
                sizes="100vw"
                style={{
                  width: '80px',
                  height: 'auto',
                  objectFit: 'contain',
                  margin: '22px',
                }}
              />
              <p className="mx-4 text-center">
                {translationFallback(
                  'pricingDataText',
                  t('pricingDataText'),
                  'In-depth pricing data and sales statistics'
                )}
              </p>
            </div>
            <div
              className="col d-flex flex-column justify-content-center align-items-center"
              style={{
                background: '#FFFFFF1A',
                borderRadius: '20px',
                width: '359px',
                height: '193px',
              }}
            >
              <Image
                src="/images/sell-guide/gri1.svg"
                alt="example"
                width={0}
                height={0}
                sizes="100vw"
                style={{
                  width: '80px',
                  height: 'auto',
                  objectFit: 'contain',
                  margin: '22px',
                }}
              />
              <p className="mx-4 text-center">
                {translationFallback(
                  'quickSellText',
                  t('quickSellText'),
                  'Sell and buy in just a few seconds, no hassle'
                )}
              </p>
            </div>
          </div>
        </div>
        <SearchInGuides
          becomeTitle={translationFallback(
            'becomeSellerText',
            t('becomeSellerText'),
            'Become a Seller right now'
          )}
          becomeCallToAct={translationFallback(
            'sellFirstBottleText',
            t('sellFirstBottleText'),
            'Sell your first bottle today'
          )}
          userId={user?.id ?? null}
        />

        <div id="seller-Section12" className="container px-2 px-sm-8 py-6 py-sm-4">
          <div className={`h3 ${styles.bold} tw-mb-6`}>
            {translationFallback('faqTitle', t('faqTitle'), 'Frequently asked questions')}
          </div>

          <Accordion defaultActiveKey="0" className={'my-4'}>
            <h2 className={`tw-my-6`}>
              {translationFallback(
                'faqTitle_first_header',
                t('faqTitle_first_header'),
                'Verkauf & Angebotsprozess'
              )}
            </h2>
            <Accordion.Item eventKey="0">
              <Accordion.Header>
                <b>
                  {translationFallback(
                    'askDurationQuestion',
                    t('askDurationQuestion'),
                    'How long does a bid last?'
                  )}
                </b>
              </Accordion.Header>

              <Accordion.Body>
                {translationFallback(
                  'askDurationAnswer',
                  t('askDurationAnswer'),
                  'Your ask lasts for 60 days, yet you can select other periods for your bids as well.'
                )}
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="1">
              <Accordion.Header>
                <b>
                  {translationFallback(
                    'cancelAskQuestion',
                    t('cancelAskQuestion'),
                    'How can I cancel my ask?'
                  )}
                </b>
              </Accordion.Header>

              <Accordion.Body>
                {translationFallback(
                  'cancelAskAnswer',
                  t('cancelAskAnswer'),
                  'Simply go to "Selling", go to your "open Asks" and delete your Ask.'
                )}
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="2">
              <Accordion.Header>
                <b>
                  {translationFallback(
                    'editAskQuestion',
                    t('editAskQuestion'),
                    'Kann ich mein Angebot nachträglich ändern?'
                  )}
                </b>
              </Accordion.Header>

              <Accordion.Body>
                {translationFallback(
                  'editAskAnswer',
                  t('editAskAnswer'),
                  'Ja, das ist möglich! Loggen Sie sich einfach in Ihren Account ein, gehen Sie zu Ihren aktiven Angeboten und wählen Sie das Angebot aus, das Sie anpassen möchten. Dort können Sie alle Änderungen vornehmen und das Angebot aktualisieren.'
                )}
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="3">
              <Accordion.Header>
                <b>
                  {translationFallback(
                    'multipleAskQuestion',
                    t('multipleAskQuestion'),
                    'Kann ich mehrere Flaschen gleichzeitig anbieten?'
                  )}
                </b>
              </Accordion.Header>

              <Accordion.Body>
                {translationFallback(
                  'multipleAskAnswer',
                  t('multipleAskAnswer'),
                  'Ja, auf unserer Plattform können Sie mehrere Flaschen gleichzeitig anbieten. Egal, ob Sie als privater oder gewerblicher Verkäufer aktiv sind – das Inserieren mehrerer Flaschen ist ganz einfach und ermöglicht es Ihnen, Ihr gesamtes Angebot auf einmal darzustellen.'
                )}
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="4">
              <Accordion.Header>
                <b>
                  {translationFallback(
                    'faqs.faq1q',
                    t('faqs.faq1_q'),
                    'How Spiritory is going to pay me and when?'
                  )}
                </b>
              </Accordion.Header>

              <Accordion.Body>
                {translationFallback(
                  'faqs.faq1_a',
                  t('faqs.faq1_a'),
                  'Payments are initiated 3–5 business days after the buyer confirms receipt and the transaction is marked as complete. The amount is securely transferred to the bank account linked to your seller profile.'
                )}
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>

          <Accordion defaultActiveKey="0" className={'my-4'}>
            <h2 className={`tw-my-6`}>
              {translationFallback(
                'faqTitle_second_header',
                t('faqTitle_second_header'),
                'Produktseite & Authentifizierung'
              )}
            </h2>

            <Accordion.Item eventKey="0">
              <Accordion.Header>
                <b>
                  {translationFallback(
                    'bottlenotmarketplaceQuestion',
                    t('bottlenotmarketplaceQuestion'),
                    'Was mache ich, wenn meine Flasche nicht auf dem Marktplatz ist?'
                  )}
                </b>
              </Accordion.Header>

              <Accordion.Body>
                <div
                  dangerouslySetInnerHTML={{
                    __html: translationFallback(
                      'bottlenotmarketplaceAnswer',
                      t('bottlenotmarketplaceAnswer'),
                      'Falls deine gesuchte Flasche auf unserem Marktplatz fehlt, kannst du sie uns schnell und unkompliziert vorschlagen! Folge einfach diesen Schritten:<br><br>' +
                        '    <ol><li><b>Einloggen:</b> Melde dich auf deinem Spiritory-Konto an.</li>' +
                        '    <li><b>Flasche vorschlagen:</b> Gehe auf die Seite und klicke auf "Produkt vorschlagen". Hier kannst du alle Informationen zur fehlenden Flasche eintragen.<br>' +
                        '</li></ol><b>Folgende Felder sind auszufüllen:</b><br><ul>' +
                        '    <li><b>Produktkategorie:</b> Wähle die passende Kategorie, z. B. Whisky.</li>' +
                        '    <li><b>Produktname:</b> Gib den vollständigen Namen der Flasche ein.</li>' +
                        '    <li><b>Marke:</b> Wähle eine bestehende Marke aus oder füge eine neue hinzu.</li>' +
                        '</ul><b>Optionale Informationen:</b> Du kannst die folgenden Details ergänzen, um uns bei der Prüfung deines Vorschlags zu unterstützen:<br><ul>' +
                        '    <li><b>Jahr abgefüllt / Jahrgang</b></li>' +
                        '    <li><b>Einzelhandelspreis (UVP):</b> Bitte, gerundet ohne Dezimalstellen.</li>' +
                        '    <li><b>Veröffentlichungsdatum:</b> Format: TT/MM/JJJJ.</li>' +
                        '    <li><b>Externer Link:</b> Falls du einen Link zu einer anderen Website mit weiteren Infos hast, kannst du diesen hier angeben.<br>' +
                        '</ul>Sobald du alles eingegeben hast, prüft unser Team den Vorschlag und fügt die Flasche schnellstmöglich hinzu.'
                    ),
                  }}
                ></div>
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="1">
              <Accordion.Header>
                <b>
                  {translationFallback(
                    'what_authentication_question',
                    t('what_authentication_question'),
                    'Was ist der Authentifizierungsprozess, und wie lange dauert er?'
                  )}
                </b>
              </Accordion.Header>

              <Accordion.Body>
                <div
                  dangerouslySetInnerHTML={{
                    __html: translationFallback(
                      'what_authentication_response',
                      t('what_authentication_response'),
                      'Unser Ziel bei Spiritory ist es, Ihnen höchste Sicherheit und Vertrauen beim Kauf Ihrer Flasche zu bieten – genauso, als würden Sie in einem Spirituosengeschäft kaufen. Hier ist unser Authentifizierungsprozess:<br>' +
                        '    <br><ol>' +
                        '<li><b>Authentifizierung</b><br>' +
                        'Jedes Produkt wird von unserem Expertenteam überprüft, um sicherzustellen, dass es authentisch und frei von Fälschungen ist. Dies schützt Sie als Käufer und garantiert, dass nur echte Flaschen verkauft werden.<br>' +
                        '    </li><br><li><b>Zustand der Flasche und Verpackung</b><br>' +
                        'Wir achten darauf, dass sowohl die Flasche als auch die Verpackung in einwandfreiem Zustand sind. Unser Team prüft diese Aspekte sorgfältig, um sicherzustellen, dass Sie ein qualitativ hochwertiges Produkt erhalten.<br>' +
                        '    </li><br><li><b>Einreichen der Bilder</b><br>' +
                        'Um die Flasche zu authentifizieren, benötigt unser Team Bilder von der Flasche und ihrer Verpackung. Diese Bilder helfen dabei, die Echtheit und den Zustand des Produkts zu bewerten.<br>' +
                        '    </li><br><li><b>Prüfung durch das Expertenteam</b><br>' +
                        'Nachdem die Bilder eingereicht wurden, beginnt unser Expertenteam die Prüfung. Innerhalb von 48 Stunden erhalten Sie eine Rückmeldung, ob die Authentifizierung erfolgreich war.<br>' +
                        '    </li><br><li><b>Bezahlung und Versand</b><br>' +
                        'Nach erfolgreicher Authentifizierung können Sie Ihre Zahlung vornehmen. Anschließend erhält der Verkäufer die Versandinformationen, und die Flasche wird an Sie versendet.<br>' +
                        '</li></ol>Dieser Prozess bietet Ihnen ein hohes Maß an Sicherheit und stellt sicher, dass Sie auf Spiritory nur echte und einwandfreie Flaschen erhalten.'
                    ),
                  }}
                ></div>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </div>
        <div id="seller-Section13" className="position-relative my-8" style={{ color: '#FFFFFF' }}>
          <Image
            src="/images/sell-guide/last.jpg"
            alt="example"
            width={0}
            height={0}
            sizes="100vw"
            style={{ width: '100%', height: '352px', objectFit: 'cover' }}
            className=""
          />
          <div className="position-absolute top-50 start-50 translate-middle text-center">
            <h2 className="h2">
              {translationFallback(
                'createAccountText',
                t('createAccountText'),
                'You don’t have an account yet?'
              )}
            </h2>
            <h2>
              {translationFallback(
                'createAccountButton',
                t('createAccountButton'),
                'Create it right now'
              )}
            </h2>
            <button
              className={`btn btn-light font-size-20 lh-25 mt-6 ${styles.whiteBtn} ${styles.customButton2}`}
            >
              <div
                onClick={() => {
                  if (user) {
                    router.push('/dashboard');
                  } else {
                    router.push('/auth/sign-up');
                  }
                }}
                style={{ textDecoration: 'none' }}
              >
                {translationFallback(
                  'createAccountButton',
                  t('createAccountButton'),
                  'Sign up Now'
                )}
              </div>
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export const getStaticProps = getBasicStaticSideProps(['common', 'seller_guide', 'index']);

export default SellerGuide;
